import React from "react"
import type { UseMutationResult } from "@tanstack/react-query"
import { TimelineStep } from "@/chargeDetection/components"

export interface StepConfig<TResponse> {
  readonly id: number
  readonly title: string
  readonly ResultComponent: React.ComponentType<{ data: TResponse }>
  readonly mutation: UseMutationResult<TResponse, Error, any, unknown>
  readonly fallbackMutation?: UseMutationResult<TResponse, Error, any, unknown>
}

export interface StepPipelineProps<TConfigs extends readonly StepConfig<any>[]> {
  configs: TConfigs
}

export function StepPipeline<TConfigs extends readonly StepConfig<any>[]>({
  configs,
}: StepPipelineProps<TConfigs>) {
  return (
    <div className="w-full">
      {configs.map((config, index) => {
        const isLast = index === configs.length - 1
        const { status, data, error } = config.mutation

        // Check if fallback is active
        const fallbackStatus = config.fallbackMutation?.status
        const isFallbackPending = fallbackStatus === 'pending'
        const isFallbackSuccess = fallbackStatus === 'success'
        const fallbackError = config.fallbackMutation?.error
        const fallbackData = config.fallbackMutation?.data

        // Determine effective status and data
        const effectiveStatus = isFallbackPending ? 'pending' :
                               isFallbackSuccess ? 'success' :
                               status
        const effectiveData = isFallbackSuccess ? fallbackData : data
        const effectiveError = fallbackError?.message || error?.message

        return (
          <TimelineStep
            key={config.id}
            title={config.title}
            status={effectiveStatus}
            error={effectiveError}
            isLast={isLast}
            isFallback={isFallbackPending || isFallbackSuccess}
          >
            {effectiveStatus === 'success' && effectiveData && (
              <config.ResultComponent data={effectiveData} />
            )}
          </TimelineStep>
        )
      })}
    </div>
  )
}
